import { foldName } from './suggest';

export const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export const COLORS = [
  'black', 'blue', 'brown', 'gray', 'green', 'pink', 'purple', 'red', 'white', 'yellow',
];

export const CATEGORIES = [
  ['legendary', 'Legendary'],
  ['mythical', 'Mythical'],
  ['baby', 'Baby'],
  ['evolved', 'Fully evolved'],
  ['unevolved', 'Not fully evolved'],
];

export const STAT_FIELDS = [
  ['hp', 'HP', 'hp'],
  ['attack', 'Attack', 'atk'],
  ['defense', 'Defense', 'def'],
  ['specialAttack', 'Sp. Atk', 'spa'],
  ['specialDefense', 'Sp. Def', 'spd'],
  ['speed', 'Speed', 'spe'],
  ['total', 'BST', 'bst'],
];

const PARAM_KEYS = [
  'mode', 'join', 'q', 'letter', 'types', 'typeMode', 'gen', 'from', 'to', 'cat', 'color',
  'hmin', 'hmax', 'wmin', 'wmax', 'sort', 'dir', 'ability', 'abilityMode', 'move', 'moveMode',
  'hpmin', 'hpmax', 'atkmin', 'atkmax', 'defmin', 'defmax', 'spamin', 'spamax',
  'spdmin', 'spdmax', 'spemin', 'spemax', 'bstmin', 'bstmax', 'mega',
];

export function emptyFilters(mode = 'basic') {
  return {
    mode,
    join: 'and',
    query: '',
    letter: '',
    types: [],
    typeMode: 'any',
    generation: 'all',
    dexMin: '',
    dexMax: '',
    categories: [],
    color: '',
    heightMin: '',
    heightMax: '',
    weightMin: '',
    weightMax: '',
    statMin: {},
    statMax: {},
    sort: 'dex',
    sortDir: 'asc',
    abilities: [],
    abilityMode: 'any',
    moves: [],
    moveMode: 'any',
    megas: '',
  };
}

export function fromLegacy({ query = '', type = 'all', generation = 'all' } = {}) {
  const filters = emptyFilters();
  filters.query = query || '';
  filters.generation = generation && generation !== 'all' ? String(generation) : 'all';
  if (type && type !== 'all') filters.types = [String(type).toLowerCase()];
  return filters;
}

function list(value) {
  return String(value || '').split(',').map((part) => part.trim()).filter(Boolean);
}

function keyFor(prefix, name) {
  return prefix ? `${prefix}_${name}` : name;
}

function bounded(value, min, max) {
  if (value === '' || value === null || value === undefined) return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return String(Math.min(max, Math.max(min, number)));
}

export function readFilters(params, prefix = '') {
  const get = (name) => params.get(keyFor(prefix, name)) || '';
  const filters = emptyFilters();
  const mode = get('mode');
  filters.join = get('join') === 'or' ? 'or' : 'and';
  filters.query = get('q');
  const letter = get('letter').toUpperCase();
  filters.letter = LETTERS.includes(letter) ? letter : '';
  filters.types = list(get('types')).map((type) => type.toLowerCase());
  filters.typeMode = get('typeMode') === 'both' ? 'both' : 'any';
  const generation = get('gen');
  filters.generation = generation && generation !== 'all' ? generation : 'all';
  filters.dexMin = get('from');
  filters.dexMax = get('to');
  filters.categories = list(get('cat'));
  filters.color = get('color').toLowerCase();
  filters.heightMin = get('hmin');
  filters.heightMax = get('hmax');
  filters.weightMin = get('wmin');
  filters.weightMax = get('wmax');
  filters.sort = get('sort') || 'dex';
  filters.sortDir = get('dir') === 'desc' ? 'desc' : 'asc';
  filters.abilities = list(get('ability'));
  filters.abilityMode = get('abilityMode') === 'all' ? 'all' : 'any';
  filters.moves = list(get('move'));
  filters.moveMode = get('moveMode') === 'all' ? 'all' : 'any';
  const megas = get('mega');
  filters.megas = megas === 'hide' || megas === 'only' ? megas : '';
  STAT_FIELDS.forEach(([, , param]) => {
    const min = get(`${param}min`);
    const max = get(`${param}max`);
    if (min !== '') filters.statMin[param] = min;
    if (max !== '') filters.statMax[param] = max;
  });
  const advanced = mode === 'advanced'
    || filters.sort !== 'dex'
    || filters.sortDir === 'desc'
    || filters.abilities.length > 0
    || filters.moves.length > 0
    || Object.keys(filters.statMin).length > 0
    || Object.keys(filters.statMax).length > 0;
  filters.mode = mode === 'basic' && !advanced ? 'basic' : (advanced || mode === 'advanced' ? 'advanced' : 'basic');
  if (mode === 'advanced') filters.mode = 'advanced';
  return filters;
}

export function writeFilters(params, filters, prefix = '') {
  const next = new URLSearchParams(params);
  PARAM_KEYS.forEach((name) => next.delete(keyFor(prefix, name)));
  const put = (name, value) => {
    if (value === undefined || value === null || value === '') return;
    next.set(keyFor(prefix, name), String(value));
  };
  if (filters.mode === 'advanced') put('mode', 'advanced');
  if (filters.join === 'or') put('join', 'or');
  // Keep a trailing space. The ghost suggestion rewrites the field, and trimming
  // here made the next word ("mega char") stick to the end of the first Mega.
  put('q', filters.query);
  put('letter', filters.letter);
  if (filters.types.length) put('types', filters.types.join(','));
  if (filters.types.length > 1 && filters.typeMode === 'both') put('typeMode', 'both');
  if (filters.generation && filters.generation !== 'all') put('gen', filters.generation);
  put('from', filters.dexMin);
  put('to', filters.dexMax);
  if (filters.categories.length) put('cat', filters.categories.join(','));
  put('color', filters.color);
  put('hmin', filters.heightMin);
  put('hmax', filters.heightMax);
  put('wmin', filters.weightMin);
  put('wmax', filters.weightMax);
  if (filters.sort && filters.sort !== 'dex') put('sort', filters.sort);
  if (filters.sortDir === 'desc') put('dir', 'desc');
  if (filters.abilities.length) put('ability', filters.abilities.join(','));
  if (filters.abilities.length > 1 && filters.abilityMode === 'all') put('abilityMode', 'all');
  if (filters.moves.length) put('move', filters.moves.join(','));
  if (filters.moves.length > 1 && filters.moveMode === 'all') put('moveMode', 'all');
  if (filters.megas === 'hide' || filters.megas === 'only') put('mega', filters.megas);
  STAT_FIELDS.forEach(([, , param]) => {
    put(`${param}min`, filters.statMin[param] || '');
    put(`${param}max`, filters.statMax[param] || '');
  });
  return next;
}

function within(value, min, max) {
  if (typeof value !== 'number' || Number.isNaN(value)) return false;
  if (min !== '' && value < Number(min)) return false;
  if (max !== '' && value > Number(max)) return false;
  return true;
}

function matchesQuery(entry, query) {
  const text = String(query || '').trim().toLowerCase();
  if (!text) return true;
  const numeric = text.replace(/^#/, '');
  if (/^\d+$/.test(numeric)) return entry.id === Number(numeric);
  const folded = foldName(text);
  return foldName(entry.name).includes(folded) || foldName(entry.slug).includes(folded);
}

function categoryHit(entry, id) {
  if (id === 'legendary') return Boolean(entry.legendary);
  if (id === 'mythical') return Boolean(entry.mythical);
  if (id === 'baby') return Boolean(entry.baby);
  if (id === 'evolved') return entry.fullyEvolved === true;
  if (id === 'unevolved') return entry.fullyEvolved === false;
  return false;
}

function statValue(entry, param) {
  if (param === 'bst') return entry.total;
  const field = STAT_FIELDS.find(([, , key]) => key === param);
  if (!field) return undefined;
  return entry.stats?.[field[0]];
}

function knowsMove(entry, moveIndexes, learnsets, mode) {
  const known = new Set(learnsets.byId?.[String(entry.id)] || []);
  const hits = moveIndexes.map((index) => known.has(index));
  return mode === 'all' ? hits.every(Boolean) : hits.some(Boolean);
}

export function applyDexFilters(entries, filters = emptyFilters(), learnsets = null) {
  const active = [];
  const push = (enabled, test) => {
    if (enabled) active.push(test);
  };
  push(Boolean(String(filters.query || '').trim()), (entry) => matchesQuery(entry, filters.query));
  push(Boolean(filters.letter), (entry) => foldName(entry.name).startsWith(filters.letter.toLowerCase()));
  push(filters.types?.length > 0, (entry) => {
    const types = entry.types || [];
    if (filters.typeMode === 'both') return filters.types.every((type) => types.includes(type));
    return filters.types.some((type) => types.includes(type));
  });
  push(filters.generation && filters.generation !== 'all', (entry) => String(entry.generation) === String(filters.generation));
  push(filters.dexMin !== '' || filters.dexMax !== '', (entry) => within(entry.id, filters.dexMin, filters.dexMax));
  push(filters.megas === 'hide', (entry) => !entry.mega);
  push(filters.megas === 'only', (entry) => entry.mega === true);
  push(filters.categories?.length > 0, (entry) => filters.categories.some((id) => categoryHit(entry, id)));
  push(Boolean(filters.color), (entry) => entry.color === filters.color);
  push(filters.heightMin !== '' || filters.heightMax !== '', (entry) => within(entry.heightM, filters.heightMin, filters.heightMax));
  push(filters.weightMin !== '' || filters.weightMax !== '', (entry) => within(entry.weightKg, filters.weightMin, filters.weightMax));
  STAT_FIELDS.forEach(([, , param]) => {
    const min = filters.statMin?.[param] ?? '';
    const max = filters.statMax?.[param] ?? '';
    push(min !== '' || max !== '', (entry) => within(statValue(entry, param), min, max));
  });
  push(filters.abilities?.length > 0, (entry) => {
    const names = (entry.abilities || []).map((ability) => foldName(ability.name || ability));
    const wanted = filters.abilities.map((ability) => foldName(ability));
    return filters.abilityMode === 'all'
      ? wanted.every((ability) => names.includes(ability))
      : wanted.some((ability) => names.includes(ability));
  });
  const moveIndexes = (filters.moves || [])
    .map((id) => learnsets?.idToIndex?.get?.(id) ?? learnsets?.idToIndex?.[id])
    .filter((index) => index !== undefined);
  push(filters.moves?.length > 0 && learnsets && moveIndexes.length === filters.moves.length, (entry) => (
    knowsMove(entry, moveIndexes, learnsets, filters.moveMode)
  ));

  return (entries || []).filter((entry) => {
    if (!active.length) return true;
    const results = active.map((test) => test(entry));
    return filters.join === 'or' ? results.some(Boolean) : results.every(Boolean);
  });
}

export function sortDex(entries, filters = emptyFilters()) {
  const direction = filters.sortDir === 'desc' ? -1 : 1;
  const param = filters.sort && filters.sort !== 'dex' ? filters.sort : '';
  const valueOf = (entry) => {
    if (!param) return entry.id;
    if (param === 'bst' || param === 'total') return entry.total || 0;
    const field = STAT_FIELDS.find((item) => item[0] === param || item[2] === param);
    return field ? (entry.stats?.[field[0]] || 0) : entry.id;
  };
  return [...(entries || [])].sort((a, b) => {
    const primary = (valueOf(a) - valueOf(b)) * direction;
    if (primary) return primary;
    if (a.id !== b.id) return a.id - b.id;
    const aForm = a.mega || a.primal ? 1 : 0;
    const bForm = b.mega || b.primal ? 1 : 0;
    if (aForm !== bForm) return aForm - bForm;
    return String(a.slug || '').localeCompare(String(b.slug || ''));
  });
}

export function sortLabel(filters) {
  if (!filters.sort || filters.sort === 'dex') return null;
  const field = STAT_FIELDS.find((item) => item[0] === filters.sort || item[2] === filters.sort);
  if (!field) return null;
  return {
    label: field[1],
    value: (entry) => (field[2] === 'bst' ? entry.total : entry.stats?.[field[0]]),
  };
}

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES);

export function activeChips(filters) {
  const chips = [];
  if (String(filters.query || '').trim()) chips.push({ id: 'query', label: `Name: ${filters.query.trim()}` });
  if (filters.letter) chips.push({ id: 'letter', label: `Starts with ${filters.letter}` });
  if (filters.types?.length) {
    const names = filters.types.map((type) => type.charAt(0).toUpperCase() + type.slice(1));
    const joiner = filters.typeMode === 'both' ? ' + ' : ' or ';
    chips.push({ id: 'types', label: names.join(joiner) });
  }
  if (filters.generation && filters.generation !== 'all') chips.push({ id: 'generation', label: `Gen ${filters.generation}` });
  if (filters.dexMin !== '' || filters.dexMax !== '') {
    chips.push({ id: 'dex', label: `#${filters.dexMin || '1'}–#${filters.dexMax || '1025'}` });
  }
  (filters.categories || []).forEach((id) => {
    chips.push({ id: `cat:${id}`, label: CATEGORY_LABEL[id] || id });
  });
  if (filters.color) chips.push({ id: 'color', label: filters.color.charAt(0).toUpperCase() + filters.color.slice(1) });
  if (filters.heightMin !== '' || filters.heightMax !== '') {
    chips.push({ id: 'height', label: `Height ${filters.heightMin || '0'}–${filters.heightMax || '∞'} m` });
  }
  if (filters.weightMin !== '' || filters.weightMax !== '') {
    chips.push({ id: 'weight', label: `Weight ${filters.weightMin || '0'}–${filters.weightMax || '∞'} kg` });
  }
  STAT_FIELDS.forEach(([field, label, param]) => {
    const min = filters.statMin?.[param] ?? '';
    const max = filters.statMax?.[param] ?? '';
    if (min !== '' || max !== '') chips.push({ id: `stat:${param}`, label: `${label} ${min || '0'}–${max || 'max'}` });
    if (filters.sort === field || filters.sort === param) {
      chips.push({ id: 'sort', label: `Sort ${label} ${filters.sortDir === 'desc' ? 'high to low' : 'low to high'}` });
    }
  });
  if (filters.abilities?.length) {
    const joiner = filters.abilityMode === 'all' ? ' + ' : ' or ';
    chips.push({ id: 'abilities', label: filters.abilities.join(joiner) });
  }
  if (filters.moves?.length) {
    const joiner = filters.moveMode === 'all' ? ' + ' : ' or ';
    chips.push({ id: 'moves', label: filters.moves.join(joiner) });
  }
  if (filters.megas === 'hide') chips.push({ id: 'megas', label: 'Hide Megas' });
  if (filters.megas === 'only') chips.push({ id: 'megas', label: 'Only Megas' });
  if (filters.join === 'or') chips.push({ id: 'join', label: 'Match any filter' });
  return chips;
}

export function clearChip(filters, id) {
  const next = {
    ...filters,
    types: [...(filters.types || [])],
    categories: [...(filters.categories || [])],
    abilities: [...(filters.abilities || [])],
    moves: [...(filters.moves || [])],
    statMin: { ...filters.statMin },
    statMax: { ...filters.statMax },
  };
  if (id === 'query') next.query = '';
  else if (id === 'letter') next.letter = '';
  else if (id === 'types') next.types = [];
  else if (id === 'generation') next.generation = 'all';
  else if (id === 'dex') {
    next.dexMin = '';
    next.dexMax = '';
  } else if (id.startsWith('cat:')) next.categories = next.categories.filter((item) => item !== id.slice(4));
  else if (id === 'color') next.color = '';
  else if (id === 'height') {
    next.heightMin = '';
    next.heightMax = '';
  } else if (id === 'weight') {
    next.weightMin = '';
    next.weightMax = '';
  } else if (id.startsWith('stat:')) {
    const param = id.slice(5);
    delete next.statMin[param];
    delete next.statMax[param];
  } else if (id === 'sort') {
    next.sort = 'dex';
    next.sortDir = 'asc';
  } else if (id === 'abilities') next.abilities = [];
  else if (id === 'moves') next.moves = [];
  else if (id === 'megas') next.megas = '';
  else if (id === 'join') next.join = 'and';
  return next;
}

export function patchFilters(filters, patch) {
  return { ...filters, ...patch };
}

export { bounded };
